import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useLocation, useSearchParams, Navigate } from "react-router-dom";
import {
  alpha,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
} from "@mui/material";
import { useQuery } from "react-query";
import logo from "../../utils/images/ridePlus.png";
import googleLogo from "../../utils/images/google-g.svg";
import AuthContext from "../../context/AuthProvider";
import axios from "../../api/axios";
import { queryClient } from "../../App";
import RequestOtp from "./requestOtp";
import Consent from "../consent/consent";
import TenantSelection from "../tenants/tenant-selection";
import { useSnackbar } from "notistack";
import { phoneRegex } from "../../utils/validationPattern";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import OtpResendButton from "../../components/otpResendButton";
import { BASE_URL, VERSION } from "../../utils/config";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppleIcon from "@mui/icons-material/Apple";
import TelegramIcon from "@mui/icons-material/Telegram";
import useTenantBranding from "../../hooks/useTenantBranding";
import {
  isProfileManagementPath,
  resolvePostAuthDestination,
  shouldShowConsentAfterAuth,
} from "../../utils/postAuthNavigation";


function SignInWithPhone({
  consentId,
  /** From consent/client redirect: show tenant mark instead of the default SSO logo. */
  consentHeaderBranding,
  tenantId: propsTenantId,
  authMethodInfo,
  enabledAuthMethods: enabledAuthMethodsProp,
  botUserName: botUserNameProp,
  googleRedirect: googleRedirectProp,
  appleRedirect: appleRedirectProp,
  onBack,
}) {

  const [openDialog, setOpenDialog] = useState(false);
  const userInput = useRef({});
  const [requestOtp, setRequestOtp] = useState(false);
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState("phone"); // "phone" or "email"
  const [isTelegramLoggedIn, setIsTelegramLoggedIn] = useState(false);
  const telegramObserverRef = useRef(null);

  // OTP cooldown hook for login attempts
  const cooldownHook = useOtpResendCooldown("login", userInput.current);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Multi-tenant add flow flags from location state
  const prefilledPhone = location.state?.prefilledPhone || "";
  const skipPhoneInput = location.state?.skipPhoneInput || false;
  const multiTenantAdd = location.state?.multiTenantAdd || false;
  const queryEnabledAuthMethods = useMemo(() => {
    const rawValues = [
      ...searchParams.getAll("enabled_auth_methods"),
      ...searchParams.getAll("enabledAuthMethods"),
      ...searchParams.getAll("auth_methods"),
      ...searchParams.getAll("authMethodKeys"),
      ...searchParams.getAll("auth_method_keys"),
    ].filter(Boolean);

    if (rawValues.length === 0) return [];
    const normalized = rawValues.flatMap((value) =>
      String(value)
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
    );
    return Array.from(new Set(normalized));
  }, [searchParams]);

  // Get Telegram params from location state or props
  const hasAuthMethodInfo = Boolean(authMethodInfo);
  const enabledAuthMethods = useMemo(() => {
    // Highest priority: explicit enabled methods passed by parent (e.g. direct access loginFlow).
    if (Array.isArray(enabledAuthMethodsProp)) {
      const normalized = enabledAuthMethodsProp
        .map((m) => String(m).trim().toLowerCase())
        .filter(Boolean);
      const unique = Array.from(new Set(normalized));
      return ["phone", ...unique.filter((m) => m !== "phone")];
    }

    // When we come from `loginFlow` step 3, `authMethodInfo` is tenant-specific.
    // In that case, ignore any stale `location.state.*` coming from the original SSO link.
    if (hasAuthMethodInfo) {
      const methods = [];
      if (authMethodInfo?.telegram_bot_user_name) methods.push("telegram");
      if (authMethodInfo?.google_redirect_url) methods.push("google");
      if (authMethodInfo?.apple_redirect_url) methods.push("apple");
      return ["phone", ...methods]; // Phone is default
    }

    // Fallback for direct `consentRoute` usage where `authMethodInfo` isn't passed.
    const methods =
      location.state?.enabledAuthMethods?.length > 0
        ? location.state.enabledAuthMethods
        : queryEnabledAuthMethods;
    const unique = Array.from(new Set(methods.map((m) => String(m).toLowerCase())));
    return ["phone", ...unique.filter((m) => m !== "phone")];
  }, [
    enabledAuthMethodsProp,
    hasAuthMethodInfo,
    location.state?.enabledAuthMethods,
    queryEnabledAuthMethods,
    authMethodInfo,
  ]);

  const botUserName =
    botUserNameProp ??
    (hasAuthMethodInfo
      ? authMethodInfo?.telegram_bot_user_name || ""
      : location.state?.botUserName ||
        searchParams.get("bot_user_name") ||
        searchParams.get("botUserName") ||
        "");
  const googleRedirect =
    googleRedirectProp ??
    (hasAuthMethodInfo
      ? authMethodInfo?.google_redirect_url || ""
      : location.state?.googleRedirect ||
        searchParams.get("google_redirect") ||
        searchParams.get("googleRedirect") ||
        "");
  const appleRedirect =
    appleRedirectProp ??
    (hasAuthMethodInfo
      ? authMethodInfo?.apple_redirect_url || ""
      : location.state?.appleRedirect ||
        searchParams.get("apple_redirect") ||
        searchParams.get("appleRedirect") ||
        "");
  const consentIdFromState =
    location.state?.consentId ||
    consentId ||
    searchParams.get("consent_id") ||
    searchParams.get("consentId");
  const tenantIdFromState =
    propsTenantId ||
    location.state?.tenantId ||
    searchParams.get("tenant_id") ||
    searchParams.get("tenantId");


  // USEFORM HOOK
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onSubmit",
  });

  // Keep userInput in sync with the form field
  userInput.current = watch("phone", "");

  // Auth / tenant context
  const {
    auth,
    persist,
    setAuth,
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    isAuthInitializing,
    removeTenantSession,
  } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();
  const tenantId =
    tenantIdFromState || currentTenant?.id || getTenantIdFromUrl();
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const clientIdForOtp =
    searchParams.get("client_id") ||
    searchParams.get("clientId") ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId") ||
    location.state?.client_id ||
    location.state?.clientId;
  const telegramConsentSegment = consentIdFromState || "none";

  // If we came from a multi-tenant add flow, prefill the phone and go straight to tenant selection
  useEffect(() => {
    if (multiTenantAdd && skipPhoneInput && prefilledPhone) {
      setValue("phone", prefilledPhone, { shouldValidate: true });
      userInput.current = prefilledPhone;
      setUserIdentifier(prefilledPhone);
      setIdentifierType("phone");
      // Always show tenant selection first in add-tenant flow
      setShowTenantSelection(true);
    }
  }, [multiTenantAdd, skipPhoneInput, prefilledPhone, setValue]);
  // const { data: identityProviders } = useQuery(
  //   ["registered_identity_providers"],
  //   () => {
  //     return axios.get(`/registeredIdentityProviders`, {
  //       params: {
  //         page: -1,
  //         ...(tenantId && { tenant_id: tenantId }),
  //       },
  //     });
  //   }
  // );

  const { isFetching, error } = useQuery(
    ["login_with_phone_otp", tenantId, clientIdForOtp],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "login",
          phone: userInput.current,
          tenant_id: tenantId,
          ...(clientIdForOtp && { client_id: clientIdForOtp }),
        },
      }),
    {
      onSuccess: () => {
        cooldownHook.startCooldown(); // Start cooldown after successful OTP request
        setRequestOtp(false);
        setOpenDialog(true);
      },
      enabled: requestOtp,
    }
  );

  const onSubmit = () => {
    if (errors?.phone === undefined && cooldownHook.canResend) {
      const tenantId = currentTenant?.id || getTenantIdFromUrl();

      // If no tenant_id in URL, show tenant selection
      if (!tenantId) {
        setUserIdentifier(userInput.current);
        setIdentifierType("phone");
        setShowTenantSelection(true);
        return;
      }

      // Otherwise proceed with normal OTP request
      setRequestOtp(true);
    }
  };

  useEffect(() => {
    localStorage.setItem("persist", persist);
    queryClient.clear();
  }, [persist]);

  useEffect(() => {
    // For normal login, clear any existing session for this tenant.
    // For multi-tenant add flow, KEEP existing sessions intact.
    if (!multiTenantAdd) {
      setAuth({});
      const tid = currentTenant?.id || getTenantIdFromUrl();
      if (tid) {
        try {
          removeTenantSession(tid);
        } catch { }
      }
      queryClient.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Telegram widget callback (official Telegram login flow)
  const telegramContainerRef = useCallback(
    (container) => {
      // Cleanup previous observer
      if (telegramObserverRef.current) {
        telegramObserverRef.current.disconnect();
        telegramObserverRef.current = null;
      }

      if (!container) return;

      if (
        !enabledAuthMethods.includes("telegram") ||
        !botUserName ||
        !tenantId ||
        !BASE_URL ||
        !VERSION
      ) {
        return;
      }

      const existingScript = container.querySelector(
        'script[src*="telegram-widget"]'
      );
      if (existingScript) return;

      const cleanBotName = botUserName.startsWith("@")
        ? botUserName.substring(1)
        : botUserName;

      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-widget.js?22";
      script.async = true;
      script.setAttribute("data-telegram-login", cleanBotName);
      script.setAttribute("data-size", "large");
      script.setAttribute("data-userpic", "true");
      script.setAttribute("data-radius", "8");
      script.setAttribute(
        "data-auth-url",
        `${BASE_URL}/${VERSION}/oauth/telegram/consent/${telegramConsentSegment}/tenant/${tenantId}/authorize`
      );
      script.setAttribute("data-request-access", "write");
      container.appendChild(script);

      // Observe iframe insertion to detect logged-in state (wider widget = user has session)
      const observer = new MutationObserver(() => {
        const iframe = container.querySelector("iframe");
        if (iframe) {
          const detectLoggedIn = () => {
            const w = iframe.offsetWidth || parseInt(iframe.getAttribute("width")) || 0;
            if (w > 250) {
              setIsTelegramLoggedIn(true);
            }
          };
          iframe.addEventListener("load", detectLoggedIn);
          setTimeout(detectLoggedIn, 800);
          setTimeout(detectLoggedIn, 2000);
          observer.disconnect();
        }
      });
      observer.observe(container, { childList: true, subtree: true });
      telegramObserverRef.current = observer;
    },
    [enabledAuthMethods, botUserName, telegramConsentSegment, tenantId]
  );

  // Handle tenant selection
  const handleTenantSelected = (tenant) => {
    // After tenant selection, proceed with OTP request
    setShowTenantSelection(false);
    setOpenDialog(true);
    setRequestOtp(true);
  };

  const handleBackFromTenantSelection = () => {
    setShowTenantSelection(false);
    setUserIdentifier("");
  };

  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
      setRequestOtp(false);
    }
    queryClient.clear();
  }, [enqueueSnackbar, error, setRequestOtp]);
  const headerBrand = consentHeaderBranding;
  /** Match login step 2 tenant header: compact 60 / 80 with rounded logo box */
  const headerAvatar = headerBrand ? (
    headerBrand.logoUrl ? (
      <Avatar
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          mb: 2,
          borderRadius: 2.5,
        }}
        src={headerBrand.logoUrl}
        alt=""
        variant="rounded"
        imgProps={{ style: { objectFit: "contain" } }}
      />
    ) : (
      <Avatar
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          mb: 2,
          borderRadius: 2.5,
          fontSize: { xs: "1.5rem", sm: "2rem" },
          fontWeight: 700,
        }}
      >
        {headerBrand.name?.trim()?.charAt(0) || "?"}
      </Avatar>
    )
  ) : tenantLogo ? (
    <Avatar
      sx={{
        width: { xs: 60, sm: 80 },
        height: { xs: 60, sm: 80 },
        mb: 2,
        borderRadius: 2.5,
      }}
      src={tenantLogo}
    />
  ) : !hideDefaultBranding ? (
    <Avatar
      sx={{
        width: { xs: 60, sm: 80 },
        height: { xs: 60, sm: 80 },
        mb: 2,
        borderRadius: 2.5,
      }}
      src={logo}
    />
  ) : null;

  // RENDER
  if (isAuthInitializing && persist) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress size={40} sx={{ color: "secondary.main" }} />
      </Box>
    );
  }

  if (auth?.access_token) {
    if (shouldShowConsentAfterAuth(redirectUri)) {
      return <Consent consentId={consentIdFromState} />;
    }
    const postAuthDest = resolvePostAuthDestination({
      redirectUri,
      from: location.state?.from,
    });
    if (postAuthDest && isProfileManagementPath(postAuthDest.pathname)) {
      return <Navigate to={postAuthDest} replace />;
    }
  }

  return showTenantSelection ? (
    <TenantSelection
      userIdentifier={userIdentifier}
      identifierType={identifierType}
      onBack={handleBackFromTenantSelection}
      onTenantSelected={handleTenantSelected}
    />
  ) : (
    <>
      {openDialog ? (
        <RequestOtp
          openDialog={openDialog}
          userInput={userInput.current}
          setOpenDialog={setOpenDialog}
          cooldownHook={cooldownHook}
        />
      ) : (
        <Dialog
          open={!openDialog}
          maxWidth="xs"
          fullWidth
          scroll="body"
          sx={{
            "& .MuiDialog-paper": {
              boxShadow: theme => theme.palette.mode === 'light'
                ? `0px 8px 32px ${alpha(theme.palette.common.black, 0.1)}`
                : `0px 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
              borderRadius: 3,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.15)",
              backdropFilter: "blur(2px)",
            },
          }}
          PaperProps={{
            sx: {
              width: { xs: "calc(100% - 32px)", sm: "450px" },
              maxWidth: "450px",
              m: { xs: 2, sm: 3 },
            },
          }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <DialogContent
            sx={{
              px: 3,
              pt: 1,
              pb: 2,
            }}
          >
            {onBack && (
              <IconButton  
                onClick={onBack}
                sx={{ position: 'absolute', left: 8, top: 8, color: 'text.secondary' }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box

              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              {headerAvatar}
              <Typography
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                  fontWeight: 600,
                  color: "text.primary",
                  textAlign: "center",
                }}
              >
                {headerBrand || hideDefaultBranding ? "Sign in" : "Sign In with SSO"}
              </Typography>
            </Box>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",
                pattern: {
                  value: phoneRegex,
                  message: "Invalid phone number format",
                },
              }}
              render={({ field }) => (
                <TextField
                  size="small"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "divider",
                        borderWidth: 1.5,
                      },
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: 14, sm: 15 },
                      fontWeight: 500,
                    },
                  }}
                  fullWidth
                  label="Phone Number"
                  placeholder="Enter your phone number"
                  autoFocus={!skipPhoneInput}
                  helperText={errors?.phone ? errors?.phone?.message : ""}
                  error={errors?.phone}
                  disabled={skipPhoneInput}
                  {...field}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 3,
              pb: 3,
              pt: 1,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="small"
              disabled={isFetching || !cooldownHook.canResend}
              sx={{
                py: 1,
                mb: 2,
                bgcolor: "secondary.main",
                color: "secondary.contrastText",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                boxShadow: "0 3px 5px 2px rgba(29, 36, 38, .2)",
                "&:hover": {
                  bgcolor: "secondary.dark",
                  transform: "translateY(-1px)",
                  boxShadow: theme => `0 4px 8px 2px ${alpha(theme.palette.secondary.main, 0.3)}`,
                },
                "&:disabled": {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {cooldownHook.isOnCooldown
                ? `Login (${cooldownHook.formattedTime})`
                : "Login"}
              {isFetching && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "secondary.contrastText",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Button>
            {(cooldownHook.isOnCooldown || cooldownHook.isBlocked) && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (theme) => theme.palette.mode === 'light' ? "primary.lighter" : alpha(theme.palette.primary.main, 0.1),
                  border: "1px solid",
                  borderColor: "primary.main",
                }}
              >
                <OtpResendButton
                  cooldownHook={cooldownHook}
                  onResend={() => setRequestOtp(true)}
                  isLoading={isFetching}
                  size="small"
                  showMessage={true}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: { sm: "row", xs: "column" },
                justifyContent: "center",
                alignItems: "center",
                mb: 1.5,
                gap: { xs: 0, sm: 0.5 },
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                  fontWeight: 500,
                  fontSize: { xs: 14, sm: 15 },
                  color: "text.secondary",
                }}
              >
                Don't have an account?
              </Typography>
              <Button
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: 14, sm: 15 },
                  p: { xs: 0.5, sm: 1 },
                  minWidth: "auto",
                  "&:hover": {
                    bgcolor: "rgba(255, 170, 0, 0.08)",
                  },
                }}
                onClick={() =>
                  navigate(
                    `/register${location.search || ""}`,
                    { state: location.state }
                  )
                }
              >
                Create Account
              </Button>
            </Box>
            {/* {identityProviders?.data?.data?.length > 1 && (
              <>
                <Divider> Or </Divider>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {identityProviders?.data?.data?.map((IP) => (
                    <Button
                      key={IP?.id}
                      fullWidth
                      size="large"
                      variant="contained"
                      sx={{
                        backgroundColor: "primary.paper",
                        textTransform: "none",
                        mb: 1.5,
                        borderRadius: 6,
                      }}
                    >
                      <Avatar
                        sx={{ width: 30, height: 30, mr: 1 }}
                        src={IP.logo_uri}
                      />
                      <Typography
                        sx={{
                          textAlign: "center",
                          mt: 0.5,
                          fontSize: 14,
                          fontWeight: "bold",
                        }}
                      >
                        Sign In With {IP?.name}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              </>
            )} */}
            {/* Alternative Login Methods */}
            {(enabledAuthMethods.includes("google") && googleRedirect) ||
              (enabledAuthMethods.includes("apple") && appleRedirect) ||
              (enabledAuthMethods.includes("telegram") && botUserName) ? (
              <>
                <Divider
                  sx={{
                    my: 2,
                    fontSize: 14,
                    color: "text.disabled",
                    "&::before, &::after": {
                      borderColor: "divider",
                    },
                  }}
                >
                  Or sign in with
                </Divider>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {enabledAuthMethods.includes("google") && googleRedirect && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => (window.location.href = googleRedirect)}
                      sx={{
                        py: 1,
                        textTransform: "none",
                        color: "text.secondary",
                        borderColor: "divider",
                        borderRadius: 2,
                        fontWeight: 500,
                        fontSize: 15,
                        bgcolor: 'background.default',
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                        "&:hover": {
                          bgcolor: "action.hover",
                          borderColor: "divider",
                          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
                        },
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={googleLogo}
                        alt="Google"
                        sx={{ width: 20, height: 20 }}
                      />
                      Sign in with Google
                    </Button>
                  )}

                  {enabledAuthMethods.includes("apple") && appleRedirect && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => (window.location.href = appleRedirect)}
                      sx={{
                        py: 1,
                        textTransform: "none",
                        color: "text.secondary",
                        borderColor: "divider",
                        borderRadius: 2,
                        fontWeight: 500,
                        fontSize: 15,
                        bgcolor: 'background.default',
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                        "&:hover": {
                          bgcolor: "action.hover",
                          borderColor: "divider",
                          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
                        },
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        justifyContent: "center",
                      }}
                    >
                      <AppleIcon sx={{ fontSize: 20 }} />
                      Sign in with Apple
                    </Button>
                  )}

                  {enabledAuthMethods.includes("telegram") && botUserName && (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        overflow: "hidden",
                        borderRadius: 2,
                      }}
                    >
                      {!isTelegramLoggedIn && (
                        <Button
                          fullWidth
                          variant="outlined"
                          sx={{
                            py: 1,
                            textTransform: "none",
                            color: "text.secondary",
                            borderColor: "divider",
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: 15,
                            bgcolor: 'background.default',
                            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                            "&:hover": {
                              bgcolor: "action.hover",
                              borderColor: "divider",
                              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
                            },
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <TelegramIcon sx={{ fontSize: 20, color: "#0088cc" }} />
                          Sign in with Telegram
                        </Button>
                      )}

                      <Box
                        sx={{
                          ...(isTelegramLoggedIn
                            ? {
                                py: 0.75,
                                px: 1,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.default",
                                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "&:hover": {
                                  bgcolor: "action.hover",
                                  boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
                                },
                                "& > div": {
                                  display: "flex",
                                  justifyContent: "center",
                                  width: "100%",
                                },
                                "& iframe": {
                                  maxWidth: "100%",
                                },
                              }
                            : {
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                opacity: 0,
                                cursor: "pointer",
                                "& > div": {
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                },
                                "& iframe": {
                                  width: "100% !important",
                                  height: "100% !important",
                                  cursor: "pointer",
                                },
                              }),
                        }}
                      >
                        <div ref={telegramContainerRef}></div>
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            ) : null}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
export default SignInWithPhone;
